import React from "react";
import { pct1, toNum } from "../../../../utils/perfFormat";
import { usePhoneTheme } from "../../useIsPhone";
import { ROLE } from "./chartTokens";

// A pair of vertical bars in a small column card (the team's Position
// Depth: the squad's share in bm-orange beside the peers' in grey, each
// value printed over its bar, a footer line, and a critical border when the
// position is a gap).
//
//   ColumnPair({ label, code, bars, scale = 60, footer, alert, format, phone })
//
// bars    [{ key, label, value, color }] (the squad, then the peers;
//         colours default to ROLE.self / ROLE.peer)
// scale   the value that fills the column (the mock uses max(60, largest))
// footer  the line under the bars ("6 in rotation · peers 2.9")
// alert   a missing or thin position: bm-critical border
// format  the printed values (default "73.2%"; a missing value reads "–")
// Desktop bars are 110px tall, phones 96px.

const cx = (...parts) => parts.filter(Boolean).join(" ");

export const ColumnPair = ({ label, code, bars = [], scale = 60, footer, alert = false, format = pct1, phone: phoneProp }) => {
  const themePhone = usePhoneTheme();
  const phone = phoneProp ?? themePhone;
  const height = (v) => {
    const n = toNum(v);
    if (n === null || !scale) return 0;
    return Math.max(0, Math.min(100, (n / scale) * 100));
  };
  const summary = [label, ...bars.map((b) => `${b.label} ${format(b.value)}`), footer].filter(Boolean).join(", ");
  return (
    <div className={cx("flex min-w-0 flex-col rounded-[8px] border bg-bm-subtle", phone ? "p-[10px]" : "p-3", alert ? "border-bm-critical/50" : "border-bm-border")}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={cx("min-w-0 truncate text-bm-ink", phone ? "bm-h-xs" : "bm-h-s")}>{label}</span>
        {code && <span className="bm-label-xs text-bm-muted">{code}</span>}
      </div>
      <div role="img" aria-label={summary} className={cx("mt-[22px] flex items-end gap-2 border-b border-bm-border", phone ? "h-24" : "h-[110px]")}>
        {bars.map((b, i) => (
          <span
            key={b.key || i}
            className="relative block min-h-[2px] flex-1 rounded-t-[4px]"
            style={{ height: `${height(b.value)}%`, background: b.color || (i === 0 ? ROLE.self : ROLE.peer) }}
          >
            <span aria-hidden="true" className="absolute -top-[17px] left-0 right-0 text-center font-archivo text-[10px] font-bold leading-[14px] text-bm-ink">
              {format(b.value)}
            </span>
          </span>
        ))}
      </div>
      {footer && <span className={cx("mt-2 text-bm-muted", phone ? "bm-body-xs" : "bm-body-s")}>{footer}</span>}
    </div>
  );
};

export default ColumnPair;
